import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { capitalize, get, set } from 'lodash';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import {
  AUS_POSTCODE_REGEX,
  NAME_REGEX,
  USA_POSTCODE_REGEX,
} from '@/modules/common/utils/regex.ts';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countriesGroup1, countryOptions } from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { localityOptions } from '@/modules/entity/static/locality.ts';
import { streetTypeOptions, streetTypeOptionsUSA } from '@/modules/entity/static/street-types.ts';
import { titleOptions } from '@/modules/entity/static/titles.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { ChangeShareholderComponentProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { CalendarInput, Select, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { UpdateEntityRequest } from 'api';

interface PersonIndividualDetailsFormik {
  first_name: string;
  last_name: string;
  title: string;
  middle_name: string;
  gender: string;
  date_of_birth: string;
  country: string;
  unit_number: string;
  street_number: string;
  street_name: string;
  street_type: string;
  locality: string;
  suburb: string;
  state: string;
  postcode: string;
  social_security_number: string;
  type: 'individual';
  index?: string;
}

const validationSchema = yup.object({
  gender: yup.string().trim(), //.required('This field is required'),
  first_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  last_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  title: yup.string().trim().required('This field is required'),
  middle_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .nullable()
    .optional(),
  date_of_birth: yup.string().trim().required('This field is required'),
  country: yup.string().trim().required('This field is required'),
  unit_number: yup.string().trim(),
  street_number: yup.string().trim().required('This field is required'),
  street_name: yup.string().trim().required('This field is required'),
  street_type: yup.string().trim().required('This field is required'),
  locality: yup
    .string()
    .trim()
    .when('country', (field, schema) => {
      if (countriesGroup1.includes(field[0])) {
        //For GBR
        return schema.required('This field is required');
      }

      return schema;
    }),
  suburb: yup.string().trim().required('This field is required'),
  state: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS') {
        return schema.required('This field is required');
      }

      return schema.nullable();
    }),
  social_security_number: yup.string().trim(),
  postcode: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS' || country === 'NZL') {
        return schema.matches(AUS_POSTCODE_REGEX, 'Invalid postcode format, e.g. 2000');
      }

      if (country === 'USA') {
        return schema.matches(USA_POSTCODE_REGEX, 'Invalid postcode format, e.g. 20001');
      }

      return schema;
    })
    .required('This field is required'),
});

const initialValues: PersonIndividualDetailsFormik = {
  first_name: '',
  last_name: '',
  title: '',
  middle_name: '',
  gender: '',
  date_of_birth: '',
  country: '',
  unit_number: '',
  street_number: '',
  street_name: '',
  street_type: '',
  suburb: '',
  state: '',
  postcode: '',
  social_security_number: '',
  locality: '',
  type: 'individual',
};

export const PersonIndividualHolderDetails = ({
  index,
  title,
  moveForward,
  entity_type_prefix,
  person_details_key,
  allowSkip,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const generateInitialValues = (): PersonIndividualDetailsFormik => {
    if (person_details_key) {
      return {
        ...initialValues,
        ...get(
          dataHolder?.data?.new_data?.entity_data,
          `${entity_type_prefix}.${person_details_key}.${index}`,
          {},
        ),
        country:
          get(
            dataHolder?.data?.new_data?.entity_data,
            `${entity_type_prefix}.${person_details_key}.${index}`,
            {},
          )?.country ||
          get(dataHolder?.data?.new_data?.entity_data, entity_type_prefix)?.country ||
          dataHolder?.data?.new_data?.entity_data?.country,
      };
    }
    const currentUser = dataHolder?.data?.new_data?.user_data;
    return {
      ...initialValues,
      country: dataHolder?.data?.new_data?.entity_data?.country || initialValues.country,
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name,
      ...get(dataHolder?.data?.new_data?.entity_data, entity_type_prefix),
    };
  };

  const generatePayload = (values: PersonIndividualDetailsFormik) => {
    const address = `${values.street_number ?? ''} ${values.street_name ?? ''} ${
      values.street_type ?? ''
    }, ${values.suburb ?? ''} ${values.state ?? ''}`.trim();

    if (person_details_key) {
      const subEntities = get(
        dataHolder?.data?.new_data?.entity_data,
        `${entity_type_prefix}.${person_details_key}`,
        [],
      );
      const personal = set(
        { ...dataHolder?.data?.new_data?.entity_data },
        `${entity_type_prefix}.${person_details_key}`,
        subEntities.map((subEntity: any, indexOfSubEntity: number) =>
          indexOfSubEntity === index
            ? {
                ...subEntity,
                ...values,
                address,
                middle_name: values.middle_name || null,
                type: subEntity.type || 'individual',
                index: subEntity.index || uuidv4(),
              }
            : subEntity,
        ),
      );

      return personal;
    }
    const common = set(
      {
        ...dataHolder?.data?.new_data?.entity_data,
        address,
      },
      entity_type_prefix,
      {
        ...get(dataHolder?.data?.new_data?.entity_data, entity_type_prefix),
        ...values,
        middle_name: values.middle_name || null,
        address,
      },
    );

    return common;
  };

  const formik = useFormik<PersonIndividualDetailsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    enableReinitialize: true,
    onSubmit: async values => {
      setDirty(false);
      const currentPerson = get(
        dataHolder?.data?.new_data?.entity_data,
        `${entity_type_prefix}.${person_details_key}.${index}`,
        {},
      ) as ShareholderDetails;
      if (formik.dirty || ((!currentPerson?.type || !currentPerson?.index) && person_details_key)) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: generatePayload(values) as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeSelect = (field: string, value: string) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    if (field === 'country') {
      if (value !== 'USA') {
        formik.setFieldValue('social_security_number', '');
        formik.setFieldError('social_security_number', '');
      }
      formik.setFieldValue('state', '');
      formik.setFieldError('state', '');
      formik.setFieldValue('street_type', '');
      formik.setFieldError('street_type', '');
      formik.setFieldValue('postcode', '');
      formik.setFieldError('postcode', '');
      formik.setFieldValue('locality', '');
      formik.setFieldError('locality', '');
    }
  };

  const handleChangeDate = (date: dayjs.Dayjs) => {
    formik.setFieldValue('date_of_birth', date ? date.format('YYYY-MM-DD').toString() : '');
    formik.setFieldError('date_of_birth', '');
  };

  const getDisabledDate = (date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day');

  return (
    <OnboardingHolderWrapper
      title={
        title ||
        `${person_details_key ? capitalize(person_details_key) : 'Individual'} ${
          person_details_key ? (index || index === 0) && index + 1 : ''
        } details`
      }
      titleHint={titleHint}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col gap-8">
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <Select
            isClearable
            label="gender"
            options={genderOptions}
            value={formik.values.gender}
            onChange={value => handleChangeSelect('gender', value || '')}
            error={formik.errors.gender}
          />
          <Select
            label="title"
            options={titleOptions}
            isClearable
            value={formik.values.title}
            defaultValue={formik.values.title}
            onChange={value => handleChangeSelect('title', value || '')}
            error={formik.errors.title}
          />
          <TextInputField label="first name" name={`first_name`} />
        </div>
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField label="Middle name (optional)" name={`middle_name`} />
          <TextInputField label="Last name" name={`last_name`} />
        </div>
        <CalendarInput
          label="date of birth"
          error={formik.errors.date_of_birth}
          defaultValue={
            formik.values.date_of_birth ? dayjs(formik.values.date_of_birth) : undefined
          }
          onChange={handleChangeDate}
          disabledDate={getDisabledDate}
        />
        <Select
          isClearable
          label="country"
          error={formik.errors.country}
          options={countryOptions}
          value={formik.values.country}
          onChange={value => handleChangeSelect('country', value || '')}
        />
        {formik.values.country === 'USA' && (
          <TextInputField name="social_security_number" label="social security number" />
        )}
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField label="unit number (optional)" name="unit_number" />
          <TextInputField label="street number" name="street_number" />
        </div>
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField label="street Name" name="street_name" />
          <Select
            isClearable
            label="Street Type"
            key={`street_type_country_${formik.values.country}`}
            error={formik.errors.street_type}
            options={formik.values.country === 'AUS' ? streetTypeOptions : streetTypeOptionsUSA}
            value={formik.values.street_type}
            onChange={value => handleChangeSelect('street_type', value || '')}
            positionMenu="up"
          />
        </div>
        {countriesGroup1.includes(formik.values.country) && (
          <div className="w-full flex items-start">
            <Select
              isClearable
              key={`locality_country_${formik.values.country}`}
              label="Locality"
              options={localityOptions}
              value={formik.values.locality}
              onChange={value => handleChangeSelect('locality', value || '')}
              error={formik.errors.locality}
              positionMenu="up"
            />
          </div>
        )}
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField label="suburb" name="suburb" />
          {(formik.values.country === 'AUS' || formik.values.country === 'USA') && (
            <Select
              isClearable
              label="State"
              key={`state_country_${formik.values.country}`}
              options={formik.values.country === 'AUS' ? auStateOptions : usaStateOptions}
              value={formik.values.state}
              onChange={value => handleChangeSelect('state', value || '')}
              error={formik.errors.state}
              positionMenu="up"
            />
          )}
          <TextInputField label="postcode" name="postcode" />
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
